import React from 'react'
import { FormScreen } from 'components/screen-templates/FormScreen'
import * as Yup from 'yup'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { Conditions, Quizzes } from '../../redux/quiz/types'
import { CheckboxGroup } from '../core/fields/CheckboxGroup'
import { Checkbox } from '../core/fields/Checkbox'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { evaluateCondition } from '../utils'
import { Answer } from '../../redux/quiz/slice'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'
import { GifName } from '../../data/gifs'

type Option = {
  label: string
  value: string
  fixed?: boolean
  showWhen?: Conditions
  // Selecting an exclusive option clears the others, and vice-versa.
  exclusive?: boolean
  // Options sharing an exclusiveGroup swap each other: selecting one clears the
  // rest of its group, while other options stay selected.
  exclusiveGroup?: string
}

export type MultiSelectScreenProps = {
  title: string
  description?: string
  topImage?: string
  topImageAlt?: string
  gif?: GifName
  buttonTitle?: string
  questionKey: string
  options: Option[]
  hideBackLink?: boolean
  includeWeightGoalAnswers?: boolean
  // Same copy, shown with Ziggie: Ziggie asks the title from a speech bubble
  // (the description isn't shown).
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = MultiSelectScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  conditionValues: Required<Conditions>
  quiz: Quizzes
  goalAnswersToBeSubmitted: Answer[] | undefined
}

const getVisibleOptions = (options: Option[], conditionValues: Required<Conditions>): Option[] => {
  return options.filter((option) => !option.showWhen || evaluateCondition(option.showWhen, conditionValues))
}

export const MultiSelectScreen: React.FC<Props> = (props: Props) => {
  const rawPreviousAnswer = (props.quiz as Record<string, unknown>)?.[props.questionKey]
  const previousAnswer = Array.isArray(rawPreviousAnswer)
    ? rawPreviousAnswer
    : typeof rawPreviousAnswer === 'string'
      ? rawPreviousAnswer
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined
  const visibleOptions = getVisibleOptions(props.options, props.conditionValues)
  const exclusiveValues = visibleOptions.filter((option) => option.exclusive).map((option) => option.value)

  const options = visibleOptions.map((option, index) => (
    <Checkbox
      key={index}
      name={props.questionKey}
      id={option.value}
      value={option.value}
      label={option.label}
      exclusive={option.exclusive}
      exclusiveValues={exclusiveValues}
      groupValues={
        option.exclusiveGroup
          ? visibleOptions
              .filter((other) => other.exclusiveGroup === option.exclusiveGroup && other.value !== option.value)
              .map((other) => other.value)
          : undefined
      }
    />
  ))

  return (
    <FormScreen
      topImage={props.topImage}
      topImageAlt={props.topImageAlt}
      gif={props.gif}
      screenTitle={props.title}
      description={props.description ? <ContextAwareText text={props.description} quiz={props.quiz} /> : null}
      buttonText={props.buttonTitle ?? 'Continue'}
      questionSlot={
        props.withZiggie && (
          <ZiggieAsk
            gif={props.withZiggie.gif}
            text={props.title}
            quiz={props.quiz}
            typewriter={props.withZiggie.typewriter}
            subhead={props.description}
          />
        )
      }
      form={{
        body: <CheckboxGroup name={props.questionKey}>{options}</CheckboxGroup>,
        initialValues: previousAnswer
          ? ({ [props.questionKey]: previousAnswer } as Record<string, unknown> as Record<string, string>)
          : ({ [props.questionKey]: [] } as Record<string, any>),
        validationSchema: Yup.object({
          [props.questionKey]: Yup.array().min(1, 'Please select at least one option'),
        }),
      }}
      onNextClick={props.onNextClick}
      onBackClick={props.hideBackLink ? undefined : props.onBackClick}
      goalAnswersToBeSubmitted={props.goalAnswersToBeSubmitted}
    />
  )
}
